import classNames from "classnames";
import styles from "./style.module.scss";
import CustomModal from "shared/components/common/customModal";
import { Icons } from "assets";
import moment from "moment";

export enum TransactionStatus {
    Paid,
    Pending,
}

export enum TransactionInstallments {
    Single = 1,
    Double,
}

interface Service {
    id: number;
    category: string;
    name: string;
}

interface Avatar {
    id: number;
    path: string;
    type: string;
    mime: string;
    size: string;
    created_at: string;
    updated_at: string;
}

interface User {
    id: number;
    fullname: string;
    phone_number: string;
    email: string;
    coordinates: {
        type: string;
        coordinates: number[];
    };
    address: string;
    postal_code: string;
    city: string;
    status: number;
    is_verified: boolean;
    type: number;
    app_notifications: boolean;
    email_notifications: boolean;
}

interface Receiver {
    user_id: number;
    website_url: string | null;
    bio: string;
    rating: number;
    rating_count: number;
    service_areas: string[];
    is_stripe_connected: number;
    created_at: string;
    updated_at: string;
    avatar: Avatar | null;
    user: User;
    services: Service[];
}

interface Job {
    id: number;
    title: string;
    further_info: string;
    valid_date: string;
    inspection_date_time: string;
    bid_due: string;
    last_service_date: string;
    inspect_dates: string[];
    date_from: number;
    status: number;
    payment_installments: number;
    created_at: string;
    updated_at: string;
    report: Avatar | null;
    floorplans: Avatar[];
    services: Service[];
}

export interface TransactionData {
    id: number;
    amount: number;
    current_installment: number;
    status: TransactionStatus;
    is_released: number;
    transaction_id: string;
    payment_id: string;
    created_at: string;
    updated_at: string;
    user: User;
    receiver: Receiver;
    job: Job;
}

interface Props extends ModalProps {
    transaction: TransactionData;
}

const TransactionDetailModal = ({ show, handleClose, transaction }: Props) => {
    const getStatusLabel = (status: TransactionStatus) => {
        switch (status) {
            case TransactionStatus.Paid:
                return "Paid";
            case TransactionStatus.Pending:
                return "Pending";
            default:
                return "Unknown";
        }
    };

    const getInstallmentLabel = (installments: number) => {
        switch (installments) {
            case TransactionInstallments.Single:
                return "Single Payment";
            case TransactionInstallments.Double:
                return "2 Installments";
            default:
                return `${installments} Installments`;
        }
    };

    return show ? (
        <CustomModal onClose={handleClose}>
            <div className={classNames(styles.contentContainer)}>
                {/* Header */}
                <div
                    className={classNames(
                        "flex items-center justify-between px-4 mt-4 mb-3"
                    )}
                >
                    <h3 className={styles.modalTitle}>Transaction Details</h3>
                    <div
                        className={classNames(styles.crossContainer)}
                        onClick={handleClose}
                    >
                        <Icons.Cross />
                    </div>
                </div>

                <div
                    className={classNames(styles.modalBody, "w-full flex flex-col gap-5")}
                >
                    {/* Transaction Info */}
                    <div className={styles.section}>
                        <h4 className={styles.sectionTitle}>Transaction Information</h4>
                        <div className={styles.detailsGrid}>
                            <DetailItem label="Transaction ID" value={transaction.transaction_id} />
                            <DetailItem label="Payment ID" value={transaction.payment_id} />
                            <DetailItem label="Amount" value={`$${transaction.amount}`} />
                            <DetailItem
                                label="Status"
                                value={getStatusLabel(transaction.status)}
                                badge
                                badgeType={transaction.status === TransactionStatus.Paid ? "success" : "warning"}
                            />
                            <DetailItem
                                label="Payment Released"
                                value={transaction.is_released === 0 ? "Yes" : "No"}
                                badge
                                badgeType={transaction.is_released === 0 ? "success" : "pending"}
                            />
                            <DetailItem
                                label="Installment"
                                value={`${transaction.current_installment} of ${transaction.job?.payment_installments || 1}`}
                            />
                            <DetailItem
                                label="Payment Plan"
                                value={getInstallmentLabel(transaction.job?.payment_installments)}
                            />
                            <DetailItem
                                label="Date"
                                value={moment(transaction.created_at).format("MMM DD, YYYY hh:mm A")}
                            />
                        </div>
                    </div>

                    {/* Customer Info */}
                    <div className={styles.section}>
                        <h4 className={styles.sectionTitle}>Customer Details</h4>
                        <div className={styles.detailsGrid}>
                            <DetailItem label="Name" value={transaction.user.fullname} />
                            <DetailItem label="Email" value={transaction.user.email} />
                            <DetailItem label="Phone" value={transaction.user.phone_number} />
                            <DetailItem label="Address" value={transaction.user.address} />
                            <DetailItem label="City" value={transaction.user.city} />
                            <DetailItem label="Postal Code" value={transaction.user.postal_code} />
                        </div>
                    </div>

                    {/* Supplier Info */}
                    <div className={styles.section}>
                        <h4 className={styles.sectionTitle}>Supplier Details</h4>
                        <div className={styles.detailsGrid}>
                            <DetailItem label="Name" value={transaction.receiver.user.fullname} />
                            <DetailItem label="Email" value={transaction.receiver.user.email} />
                            <DetailItem label="Phone" value={transaction.receiver.user.phone_number} />
                            <DetailItem label="Address" value={transaction.receiver.user.address} />
                            <DetailItem label="City" value={transaction.receiver.user.city} />
                            <DetailItem
                                label="Rating"
                                value={`${transaction.receiver.rating} / 5 (${transaction.receiver.rating_count} reviews)`}
                            />
                        </div>
                    </div>

                    {/* Job Info */}
                    {transaction.job && (
                        <div className={styles.section}>
                            <h4 className={styles.sectionTitle}>Job Details</h4>
                            <div className={styles.detailsGrid}>
                                <DetailItem label="Job Title" value={transaction.job.title} fullWidth />
                                <DetailItem
                                    label="Inspection Date"
                                    value={moment(transaction.job.inspection_date_time).format("MMM DD, YYYY hh:mm A")}
                                />
                                <DetailItem
                                    label="Valid Until"
                                    value={moment(transaction.job.valid_date).format("MMM DD, YYYY")}
                                />
                            </div>

                            {/* Job Services */}
                            {transaction.job.services?.length > 0 && (
                                <div className={styles.servicesSection}>
                                    <p className={styles.servicesLabel}>Services:</p>
                                    <div className={styles.servicesList}>
                                        {transaction.job.services.map((service) => (
                                            <span key={service.id} className={styles.serviceTag}>
                                                {service.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </CustomModal>
    ) : null;
};

interface DetailItemProps {
    label: string;
    value: string | number | undefined | null;
    badge?: boolean;
    badgeType?: "success" | "warning" | "pending" | "error";
    fullWidth?: boolean;
}

const DetailItem = ({ label, value, badge, badgeType, fullWidth }: DetailItemProps) => (
    <div className={classNames(styles.detailItem, { [styles.fullWidth]: fullWidth })}>
        <span className={styles.label}>{label}:</span>
        {badge ? (
            <span className={classNames(styles.badge, styles[badgeType || "success"])}>
                {value || "-"}
            </span>
        ) : (
            <span className={styles.value}>{value || "-"}</span>
        )}
    </div>
);

export default TransactionDetailModal;
